import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css, keyframes } from 'emotion';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
// import TagManager from 'react-gtm-module';

import ArrowMobile from '../assets/tab/arrow-mobile.svg';
import { BREAKPOINT_SMALL } from '../consts/breakpoints';
import { APP_DATA_TOKEN } from '../services/InjectTokens';

// #region Style
const arrow = css`
  background: url(${ArrowMobile});
  width: 5px;
  height: 8px;
  margin: auto 0 auto auto;
  @media (min-width: ${BREAKPOINT_SMALL}) {
    display: none;
  }
`;
// #endregion

interface ITabProps {
  isCompleted?: boolean;
  isSelected?: boolean;
  isBeforeSelected?: boolean;
  isAfterSelected?: boolean;
  id: number | string;
  title: string;
  name: string;
  type?: string | number;
  onClick?(id?: number | string): void;
}

@observer
export class Tab extends React.Component<ITabProps> {
  public static defaultProps = {
    isAfterSelected: false,
    isBeforeSelected: false,
    isCompleted: false,
    isSelected: false,
  };

  private appState = inject(APP_DATA_TOKEN);

  private textWrapper: React.RefObject<HTMLDivElement>;
  private text: React.RefObject<HTMLParagraphElement>;

  constructor(props: any) {
    super(props);
    this.textWrapper = React.createRef();
    this.text = React.createRef();
  }

  public render() {
    const { isCompleted, isSelected, isBeforeSelected, isAfterSelected, title, name, type } = this.props;

    return (
      <div
        className={this.computeWrapperStyle(isSelected)}
        onClick={this.onClick}
        role="tab"
        aria-selected={isSelected}
      >
        <div className={this.computeTabStyle(isCompleted, isSelected, isBeforeSelected, isAfterSelected)}>
          <div>
            <div className={this.computeTopTextStyle(isSelected)}>{title}</div>
            <div className={this.computeMarqueeStyle()} ref={this.textWrapper}>
              <p className={this.computeBottomTextStyle(isCompleted, isSelected)} ref={this.text}>
                {type && type.toString().includes('iconed') ? this.magicPersonsLength : name}
              </p>
            </div>
          </div>
          <div className={arrow} />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.forceUpdate();
  }

  @computed
  private get magicPersonsLength() {
    const { iconedMagic } = this.appState;
    const length = iconedMagic && iconedMagic.elements.length;
    let filled;

    if (iconedMagic && iconedMagic.params) {
      filled = iconedMagic && Object.keys(iconedMagic.params).length;
    } else {
      filled = 0;
    }

    return `${filled}/${length}`;
  }

  private computeWrapperStyle(isSelected?: boolean) {
    const backgroundColor = isSelected ? 'white' : 'transparent';

    return css`
      cursor: pointer;
      box-sizing: border-box;
      border: 0.5px solid #b7acd4;
      border-width: 0.5px 0 0.5px 0;
      background-color: #f6f2f3;
      :first-of-type {
        border-top: 1px solid #b7acd4;
        border-top-width: 1px;
      }
      :last-of-type {
        border-bottom: 2px solid #b7acd4;
        border-bottom-width: 2px;
      }
      @media (min-width: ${BREAKPOINT_SMALL}) {
        border: none;
        padding: 0 1px 0 1px;
        height: 60px;
        background-color: ${backgroundColor};
        :first-of-type {
          border: none;
        }
        :last-of-type {
          border: none;
        }
      }
    `;
  }

  @bind
  private onClick() {
    const { onClick, id, title } = this.props;

    if (onClick) {
      onClick(id);
    }
  }

  private computeTabStyle(
    isCompleted?: boolean,
    isSelected?: boolean,
    isBeforeSelected?: boolean,
    isAfterSelected?: boolean,
  ) {
    const borderDesktop = isCompleted ? '2px solid #72659F' : '0';
    const borderMobile = isCompleted ? '3px solid #72659F' : '0';

    const borderLeftWidth = isAfterSelected || isSelected ? '0' : '1px';
    const borderRightWidth = isBeforeSelected || isSelected ? '0' : '1px';

    return css`
      border-left: ${borderMobile};
      padding: 6px 25px 6px 32px;
      display: flex;
      @media (min-width: ${BREAKPOINT_SMALL}) {
        border-left: 0;
        padding: 10px 10px 0 10px;
        display: block;
        box-sizing: border-box;
        height: 60px;
        border-bottom: ${borderDesktop};
        position: relative;
        ::before {
          position: absolute;
          content: ' ';
          bottom: 4px;
          left: ${`-${borderLeftWidth}`};
          right: ${`-${borderRightWidth}`};
          height: 48px;
          border: 1px solid #b7acd4;
          border-width: ${`0 ${borderRightWidth} 0 ${borderLeftWidth}`};
        }
      }
    `;
  }

  private computeTopTextStyle(isSelected?: boolean) {
    const color = isSelected ? '#72659F' : '#414042';

    return css`
      height: 20px;
      font-family: Nunito, Arial;
      font-size: 13px;
      color: ${color};
      text-transform: uppercase;
    `;
  }

  private computeMarqueeStyle() {
    const text = this.text.current;
    const textWrapper = this.textWrapper.current;

    const tsOff =
      text && textWrapper && text.offsetWidth && textWrapper.offsetWidth && text.offsetWidth > textWrapper.offsetWidth
        ? `${textWrapper.offsetWidth - text.offsetWidth}px`
        : 0;

    const slide = keyframes`
      0% {
        transform: translateX(0);
      }
      50% {
        transform: translateX(${tsOff});
      }
      100% {
        transform: translateX(0);
      }
    `;

    const animation = tsOff && this.text.current ? `${slide} 4s ease-in-out infinite` : 'none';

    return css`
      white-space: nowrap;
      overflow: visible;
      box-sizing: border-box;
      position: relative;
      height: 20px;

      p {
        position: absolute;
        display: block;
      }

      @media (min-width: ${BREAKPOINT_SMALL}) {
        overflow: hidden;
        p:hover {
          animation: ${animation};
        }
      }
    `;
  }

  private computeBottomTextStyle(isCompleted?: boolean, isSelected?: boolean) {
    const color = isCompleted || isSelected ? '#72659F' : '#414042';

    return css`
      margin: 0;
      height: 20px;
      font-family: Nunito, Arial;
      font-size: 17px;
      font-weight: bold;
      color: ${color};
    `;
  }
}
